import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  applyBackgroundRemoval, getBackgroundRemovalSource, removeBackgroundPixels, restoreImageBackground,
} from "../../features/imageBackgroundRemoval";

const imageReadError = (error) => error?.name === "SecurityError"
  ? "圖片網站未允許讀取像素（CORS），無法去除背景。請改用可讀取的圖片或重新上傳原圖。"
  : "圖片無法讀取或處理，請確認圖片連結與格式後再試。";

export default function ImageBackgroundRemovalDialog({ image, sourceOverride, onApply, onClose }) {
  const dialogRef = useRef(null);
  const originalCanvas = useRef(null);
  const previewCanvas = useRef(null);
  const sourcePixels = useRef(null);
  const source = sourceOverride || getBackgroundRemovalSource(image);
  const [color, setColor] = useState(image.backgroundRemoval?.color || "#ffffff");
  const [tolerance, setTolerance] = useState(image.backgroundRemoval?.tolerance ?? 20);
  const [readyVersion, setReadyVersion] = useState(0);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [result, setResult] = useState("");

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
    dialog.querySelector("button")?.focus();
    return () => {
      if (dialog.open && typeof dialog.close === "function") dialog.close();
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  useEffect(() => {
    let active = true;
    sourcePixels.current = null;
    setStatus("loading"); setError(""); setResult("");
    if (!source) {
      setError("這張圖片尚無可讀取的原圖，請先產生圖片或補上圖片連結。");
      setStatus("error");
      return () => { active = false; };
    }
    const original = new Image();
    original.crossOrigin = "anonymous";
    original.onload = () => {
      if (!active) return;
      try {
        const width = original.naturalWidth || original.width;
        const height = original.naturalHeight || original.height;
        if (!width || !height) throw new Error("Empty image");
        const canvas = originalCanvas.current;
        canvas.width = width; canvas.height = height;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) throw new Error("Canvas unavailable");
        context.drawImage(original, 0, 0);
        const pixels = context.getImageData(0, 0, width, height);
        sourcePixels.current = { width, height, data: new Uint8ClampedArray(pixels.data) };
        setReadyVersion((version) => version + 1);
      } catch (readError) {
        setError(imageReadError(readError)); setStatus("error");
      }
    };
    original.onerror = () => {
      if (!active) return;
      setError("原圖載入失敗，可能是連結失效或圖片網站未允許跨來源讀取（CORS）。請重新上傳原圖後再試。");
      setStatus("error");
    };
    original.src = source;
    return () => { active = false; original.onload = null; original.onerror = null; };
  }, [source]);

  useEffect(() => {
    if (!sourcePixels.current) return undefined;
    setStatus("processing"); setError(""); setResult("");
    const timer = setTimeout(() => {
      try {
        const { width, height, data } = sourcePixels.current;
        const canvas = previewCanvas.current;
        canvas.width = width; canvas.height = height;
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Canvas unavailable");
        const processed = context.createImageData(width, height);
        processed.data.set(removeBackgroundPixels(data, color, tolerance));
        context.putImageData(processed, 0, 0);
        const dataUrl = canvas.toDataURL("image/png");
        if (!dataUrl.startsWith("data:image/png;base64,")) throw new Error("PNG unavailable");
        setResult(dataUrl); setStatus("ready");
      } catch (processingError) {
        setError(imageReadError(processingError)); setStatus("error");
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [readyVersion, source, color, tolerance]);

  const pickColor = (event) => {
    const pixels = sourcePixels.current;
    if (!pixels) return;
    const canvas = originalCanvas.current;
    const rect = canvas.getBoundingClientRect();
    const keyboard = event.detail === 0;
    const x = Math.max(0, Math.min(pixels.width - 1, Math.floor(keyboard ? pixels.width / 2 :
      (event.clientX - rect.left) / (rect.width || 1) * pixels.width)));
    const y = Math.max(0, Math.min(pixels.height - 1, Math.floor(keyboard ? pixels.height / 2 :
      (event.clientY - rect.top) / (rect.height || 1) * pixels.height)));
    const offset = (y * pixels.width + x) * 4;
    setColor(`#${[0, 1, 2].map((channel) => pixels.data[offset + channel].toString(16).padStart(2, "0")).join("")}`);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); onClose(); }
    if (event.key !== "Tab") return;
    const items = [...dialogRef.current.querySelectorAll("button,input,[tabindex]")]
      .filter((element) => !element.disabled && element.tabIndex >= 0);
    const first = items[0]; const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  const busy = status === "loading" || status === "processing";
  return createPortal(
    <Dialog ref={dialogRef} aria-labelledby="image-background-title" aria-modal="true"
      onKeyDown={handleKeyDown} onCancel={(event) => { event.preventDefault(); onClose(); }}>
      <Header><h2 id="image-background-title">去除圖片背景色</h2>
        <button type="button" aria-label="關閉去背景視窗" onClick={onClose}>×</button></Header>
      <Body>
        <p>選擇要變透明的顏色，也可以點選原圖取色。圖形內相同的底色也會一併去除。</p>
        <Controls>
          <label>背景顏色 <input type="color" value={color} disabled={status === "loading" || status === "error"}
            onChange={(event) => setColor(event.target.value)} /></label>
          <label>容差 <input type="range" min="0" max="80" step="1" value={tolerance}
            disabled={status === "loading" || status === "error"} onChange={(event) => setTolerance(Number(event.target.value))} />
            <output>{tolerance}</output></label>
        </Controls>
        {busy && <Busy role="status" aria-label="背景處理狀態"><Spinner aria-hidden="true" />{status === "loading" ? "正在載入原圖…" : "正在處理背景色…"}</Busy>}
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        <Images aria-busy={busy}>
          <figure><figcaption>原圖 · 點選取色</figcaption>
            <PickImage type="button" aria-label="從原圖取背景顏色" onClick={pickColor} disabled={!sourcePixels.current}>
              <canvas ref={originalCanvas} role="img" aria-label="原始圖片" />
            </PickImage></figure>
          <figure><figcaption>透明預覽</figcaption><Checker>
            <canvas ref={previewCanvas} role="img" aria-label="去除背景色後的透明預覽" />
          </Checker></figure>
        </Images>
      </Body>
      <Footer>
        {image.backgroundRemoval?.originalAsset && <button type="button" onClick={() => onApply(restoreImageBackground(image))}>還原原圖</button>}
        <Actions><button type="button" onClick={onClose}>取消</button>
          <button type="button" className="primary" disabled={status !== "ready" || !result}
            onClick={() => onApply(applyBackgroundRemoval(image, { dataUrl: result, color, tolerance }))}>套用</button></Actions>
      </Footer>
    </Dialog>, document.body,
  );
}

const spin = keyframes`to { transform: rotate(360deg); }`;
const Dialog = styled.dialog`
  box-sizing: border-box; width: min(960px, calc(100vw - 28px)); max-height: calc(100dvh - 28px);
  padding: 0; border: 1px solid var(--app-border, #d9dfe5); border-radius: 14px;
  color: var(--app-text, #26313c); background: var(--app-surface, #fff); overflow: hidden;
  &::backdrop { background: #0008; }
  &[open] { display: flex; flex-direction: column; }
  button, input { font: inherit; }
  button { cursor: pointer; border: 1px solid var(--app-border, #ced5de); border-radius: 7px; padding: 8px 14px;
    color: inherit; background: var(--app-surface-soft, #f5f7fa); }
  button:disabled { opacity: .5; cursor: default; }
  button:focus-visible, input:focus-visible { outline: 2px solid #2a79c9; outline-offset: 2px; }
`;
const Header = styled.header`display: flex; align-items: center; justify-content: space-between; padding: 15px 20px; border-bottom: 1px solid var(--app-border, #d9dfe5); h2 { margin: 0; font-size: 20px; } button { font-size: 22px; padding: 2px 10px; }`;
const Body = styled.div`padding: 0 20px 20px; overflow: auto; min-height: 0; p { font-size: 14px; line-height: 1.6; color: var(--app-subtle-text, #5b6570); }`;
const Controls = styled.div`display: flex; align-items: center; flex-wrap: wrap; gap: 14px 28px; margin-bottom: 16px; label { display: flex; align-items: center; gap: 10px; } input[type="color"] { width: 44px; height: 32px; padding: 2px; } input[type="range"] { width: min(180px, 36vw); } output { min-width: 2ch; }`;
const Images = styled.div`display: grid; grid-template-columns: minmax(0,1fr) minmax(0,1fr); gap: 16px; figure { margin: 0; min-width: 0; } figcaption { font-size: 13px; font-weight: 600; margin-bottom: 8px; } canvas { display: block; max-width: 100%; height: auto; margin: auto; } @media (max-width: 600px) { grid-template-columns: minmax(0,1fr); }`;
const PickImage = styled.button`display: flex; align-items: center; justify-content: center; width: 100%; min-height: 160px; padding: 10px !important; border-radius: 8px; background: white !important; cursor: crosshair !important;`;
const Checker = styled.div`display: flex; align-items: center; justify-content: center; min-height: 160px; padding: 10px; border: 1px solid var(--app-border, #ced5de); border-radius: 8px; background-color: #fff; background-image: linear-gradient(45deg,#dedede 25%,transparent 25%),linear-gradient(-45deg,#dedede 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#dedede 75%),linear-gradient(-45deg,transparent 75%,#dedede 75%); background-size: 20px 20px; background-position: 0 0,0 10px,10px -10px,-10px 0;`;
const Busy = styled.div`display: flex; align-items: center; gap: 8px; margin: 12px 0; font-size: 14px;`;
const Spinner = styled.span`display: inline-block; width: 16px; height: 16px; border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: ${spin} .7s linear infinite; @media (prefers-reduced-motion: reduce) { animation-duration: 1.8s; }`;
const ErrorMessage = styled.p`color: var(--app-danger-text, #b42318) !important;`;
const Footer = styled.footer`display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; padding: 14px 20px; border-top: 1px solid var(--app-border, #d9dfe5); .primary { background: #1971b9; border-color: #1971b9; color: white; }`;
const Actions = styled.div`display: flex; gap: 8px; margin-left: auto;`;
